import {
  createCommunityApi,
  createBaseApi,
  createMarketApi,
  createUserApi,
  createReferralApi,
  BaseApi,
  CommunityApi,
  MarketApi,
  UserApi,
  ReferralApi,
} from './routes';
import createTaskApi, { TaskApi } from './routes/task';
import createSSEApi, { SSEApi } from './sse';
import { Options } from 'ky';

export type ApiOptions = Options & {
  apiKey?: string;
  baseTokenAddress?: string;
};

export type ApiRouter = {
  base: BaseApi;
  community: CommunityApi;
  market: MarketApi;
  user: UserApi;
  referral: ReferralApi;
};

export type ApiInstance = {
  options: ApiOptions;
  base: BaseApi;
  community: CommunityApi;
  market: MarketApi;
  user: UserApi;
  referral: ReferralApi;
  task: TaskApi;
  sse: SSEApi;
  updateOptions: (newOptions: Partial<ApiOptions>) => void;
};

export const mergeOptions = (options: ApiOptions): ApiOptions => {
  const { apiKey, hooks = {}, baseTokenAddress } = options;
  return {
    ...options,
    hooks: {
      ...hooks,
      beforeRequest: [
        (request: Request) => {
          if (apiKey) request.headers.set('Authorization', `Bearer ${apiKey}`);
          const url = new URL(request.url);
          // Skip cache-buster for static S3-backed content endpoints
          const path = url.pathname.split('/').pop() || '';
          const staticEndpoints = ['get-banners', 'get-categories', 'get-sections', 'get-tags'];
          if (!staticEndpoints.includes(path)) {
            url.searchParams.set('t', Date.now().toString());
          }
          if (baseTokenAddress) {
            request.headers.set('BaseTokenAddress', baseTokenAddress);
            url.searchParams.set('baseTokenAddress', baseTokenAddress.toString());
          }
        },
        ...(hooks.beforeRequest || []),
      ],
    },
  };
};

export const createApi = (initialOptions: ApiOptions): ApiInstance => {
  let options: ApiOptions = mergeOptions(initialOptions);
  const instance: ApiInstance = {
    options,
    base: createBaseApi(options),
    community: createCommunityApi(options),
    market: createMarketApi(options),
    user: createUserApi(options),
    referral: createReferralApi(options),
    task: createTaskApi(options),
    sse: createSSEApi(options),
    updateOptions: (newOptions: Partial<ApiOptions>) => {
      options = mergeOptions({ ...options, ...newOptions });
      instance.options = options;
      instance.base = createBaseApi(options);
      instance.community = createCommunityApi(options);
      instance.market = createMarketApi(options);
      instance.user = createUserApi(options);
      instance.referral = createReferralApi(options);
    },
  };
  return instance;
};
